import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { httpOk, GET, POST, PUT, DELETE, FILE } from 'helpers/api'
import { notify } from 'helpers/helpers'
import DeleteModal from 'pages/parts/DeleteModal'

// Каталог типов ссылок — тот же, что BrandLinkService::TYPES на бэкенде.
// Соцсети подписаны своими названиями (они одинаковы на всех языках), остальное — через t()
const LINK_TYPES = [
	{ type: 'telegram', icon: 'uil-telegram', name: 'Telegram', placeholder: '@brand' },
	{ type: 'instagram', icon: 'uil-instagram', name: 'Instagram', placeholder: '@brand' },
	{ type: 'facebook', icon: 'uil-facebook-f', name: 'Facebook', placeholder: 'facebook.com/brand' },
	{ type: 'tiktok', icon: 'svg', name: 'TikTok', placeholder: '@brand' },
	{ type: 'youtube', icon: 'uil-youtube', name: 'YouTube', placeholder: '@brand' },
	{ type: 'linkedin', icon: 'uil-linkedin', name: 'LinkedIn', placeholder: 'linkedin.com/company/brand' },
	{ type: 'x', icon: 'svg', name: 'X (Twitter)', placeholder: '@brand' },
	{ type: 'threads', icon: 'svg', name: 'Threads', placeholder: '@brand' },
	{ type: 'whatsapp', icon: 'uil-whatsapp', name: 'WhatsApp', placeholder: '+998 90 123 45 67' },
	{ type: 'website', icon: 'uil-globe', nameKey: 'brand_link_type_website', placeholder: 'brand.uz' },
	{ type: 'phone', icon: 'uil-phone', nameKey: 'brand_link_type_phone', placeholder: '+998 90 123 45 67' },
	{ type: 'email', icon: 'uil-envelope', nameKey: 'brand_link_type_email', placeholder: 'info@brand.uz' },
	{ type: 'custom', icon: 'uil-link', nameKey: 'brand_link_type_custom', placeholder: 'https://brand.uz/jobs' },
]

const CHANNELS = ['website', 'mobile_app', 'telegram', 'qr_menu']

// В наборе Unicons проекта нет TikTok, X и Threads — для них свои SVG того же размера
const SVG_ICONS = {
	tiktok: 'M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.79a5.68 5.68 0 0 0-.77-.05A5.67 5.67 0 1 0 15.54 15.4V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48z',
	x: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
	threads: 'M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z',
}

function LinkIcon({ link, size = 18 }) {
	const meta = LINK_TYPES.find(item => item.type === link.type)

	if (link.type === 'custom' && link.icon_url) {
		return <img src={link.icon_url} alt="" style={{ width: size, height: size, objectFit: 'contain' }} />
	}

	if (SVG_ICONS[link.type]) {
		return (
			<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
				<path d={SVG_ICONS[link.type]} />
			</svg>
		)
	}

	return <i className={meta?.icon ?? 'uil-link'} style={{ fontSize: size }}></i>
}

function SortableLinkRow({ id, children }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id })

	return (
		<tr ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
			}}>
			<td className="text-center">
				<i className="uil uil-draggabledots" style={{ cursor: 'grab' }}
					{...attributes} {...listeners}></i>
			</td>
			{children}
		</tr>
	)
}

/**
 * Соцсети и контакты бренда: ссылки, которые гость видит на витрине, в приложении,
 * в Telegram-боте и внизу QR-меню. Список общий на бренд, у каждой ссылки — галочки каналов.
 * Ссылку бэкенд нормализует сам: «@brand» превращается в https://t.me/brand и т.п.
 */
function BrandLinks() {
	const { t } = useTranslation()

	const [links, setLinks] = useState([])
	const [selectedItem, setSelectedItem] = useState(null)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteId, setDeleteId] = useState(null)

	// 6px — чтобы клик по строке не превращался в перетаскивание
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
	)

	function typeName(type) {
		const meta = LINK_TYPES.find(item => item.type === type)
		if (!meta) return type

		return meta.name ?? t(meta.nameKey)
	}

	function linkCaption(link) {
		return link.title || typeName(link.type)
	}

	async function getLinks() {
		const response = await GET('/brand-links')
		if (httpOk(response)) setLinks(response.data?.data ?? [])
	}

	function openCreate(type) {
		setSelectedItem({
			'type': type,
			'title': '',
			'url': '',
			'icon': null,
			'icon_url': null,
			'channels': [...CHANNELS],
			'is_active': true,
		})
	}

	function openEdit(link) {
		setSelectedItem({ ...link, 'channels': [...(link.channels ?? [])] })
	}

	function toggleChannel(channel) {
		const channels = selectedItem.channels.includes(channel)
			? selectedItem.channels.filter(item => item !== channel)
			: [...selectedItem.channels, channel]

		setSelectedItem({ ...selectedItem, 'channels': channels })
	}

	async function save() {
		if (selectedItem.type === 'custom' && !selectedItem.title?.trim()) {
			notify(t('brand_link_title_required'))
			return
		}
		if (selectedItem.channels.length === 0) {
			notify(t('brand_link_channels_required'))
			return
		}

		const payload = {
			'id': selectedItem.id,
			'type': selectedItem.type,
			'title': selectedItem.title,
			'url': selectedItem.url,
			'icon': selectedItem.icon,
			'channels': selectedItem.channels,
			'is_active': selectedItem.is_active,
		}

		const response = selectedItem.id
			? await PUT('/brand-links', payload, { loader: true })
			: await POST('/brand-links', payload, { loader: true })

		if (httpOk(response)) {
			setSelectedItem(null)
			getLinks()
		}
	}

	// Включить/выключить прямо из таблицы, не открывая карточку
	async function toggleActive(link) {
		const response = await PUT('/brand-links', {
			...link,
			'is_active': !link.is_active,
		}, { loader: true })

		if (httpOk(response)) getLinks()
	}

	async function deleteItem() {
		const response = await DELETE(`/brand-links/${deleteId}`, {}, { loader: true })
		setModalDelete(false)
		setDeleteId(null)

		if (httpOk(response)) getLinks()
	}

	async function handleDragEnd(event) {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const oldIndex = links.findIndex(item => item.id === active.id)
		const newIndex = links.findIndex(item => item.id === over.id)
		if (oldIndex === -1 || newIndex === -1) return

		const reordered = arrayMove(links, oldIndex, newIndex)
		setLinks(reordered)

		const response = await PUT('/brand-links/order', {
			'ids': reordered.map(item => item.id),
		}, { loader: false })

		// не сохранилось — возвращаем порядок с сервера, чтобы экран не расходился с гостем
		if (!httpOk(response)) getLinks()
	}

	async function uploadIcon(e) {
		const file = e.target.files[0]
		if (!file) return

		if (!file.type?.startsWith('image/')) {
			notify(t('image2'))
			return
		}

		const formData = new FormData()
		formData.append('image', file)
		formData.append('path', 'brand_links')

		const response = await FILE('/upload-image-only', formData, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedItem({
				...selectedItem,
				'icon': response.data.url,
				'icon_url': response.data.base64,
			})
		}
	}

	useEffect(() => {
		getLinks()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const activeLinks = links.filter(item => item.is_active)
	const selectedMeta = LINK_TYPES.find(item => item.type === selectedItem?.type)

	return (
		<>
			<div className="card w-100">
				<div className="card-body">
					<div className="d-flex justify-content-between mb-2">
						<div>
							<h5 className="mb-1">{t('brand_links')}</h5>
							<small className="text-muted">{t('brand_links_hint')}</small>
						</div>
						<Dropdown>
							<Dropdown.Toggle variant="primary" className="btn-rounded">
								{t('add')}
							</Dropdown.Toggle>
							<Dropdown.Menu style={{ maxHeight: '360px', overflowY: 'auto' }}>
								{LINK_TYPES.map(item => (
									<Dropdown.Item key={item.type} onClick={() => openCreate(item.type)}>
										<span className="d-inline-flex align-items-center gap-2">
											<LinkIcon link={item} size={16} />
											{item.name ?? t(item.nameKey)}
										</span>
									</Dropdown.Item>
								))}
							</Dropdown.Menu>
						</Dropdown>
					</div>

					{/* Превью: так ряд ссылок выглядит у гостя — иконки в порядке списка */}
					{activeLinks.length > 0 &&
						<div className="d-flex align-items-center flex-wrap gap-2 mb-3">
							<small className="text-muted me-1">{t('brand_links_preview')}:</small>
							{activeLinks.map(link => (
								<a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
									title={linkCaption(link)}
									className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1">
									<LinkIcon link={link} size={16} />
									<span>{linkCaption(link)}</span>
								</a>
							))}
						</div>
					}

					{links.length > 0 &&
						<div className="text-muted mb-2">
							<i className="uil uil-draggabledots"></i>
							<span className="ms-1">{t('brand_links_drag_hint')}</span>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'width': '33px' }}></th>
									<th style={{ 'width': '40px' }}></th>
									<th style={{ 'width': '15%' }}>{t('brand_link_type')}</th>
									<th style={{ 'width': '15%' }}>{t('brand_link_title')}</th>
									<th>{t('brand_link_url')}</th>
									{CHANNELS.map(channel => (
										<th key={channel} style={{ 'width': '9%' }} className="text-center td-ellipsis"
											title={t(`brand_link_channel_${channel}`)}>
											{t(`brand_link_channel_${channel}`)}
										</th>
									))}
									<th style={{ 'width': '10%' }} className="text-center">{t('status')}</th>
									<th style={{ 'width': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									onDragEnd={handleDragEnd}>
									<SortableContext
										items={links.map(item => item.id)}
										strategy={verticalListSortingStrategy}>
										{links.map(link => (
											<SortableLinkRow key={link.id} id={link.id}>
												<td className="text-center"><LinkIcon link={link} /></td>
												<td className="td-ellipsis">
													<span className="link-primary cursor-pointer" onClick={() => openEdit(link)}>
														{typeName(link.type)}
													</span>
												</td>
												<td className="td-ellipsis" title={link.title ?? ''}>{link.title}</td>
												<td className="td-ellipsis" title={link.url}>{link.url}</td>
												{CHANNELS.map(channel => (
													<td key={channel} className="text-center">
														{link.channels?.includes(channel) && <i className="uil-check text-success"></i>}
													</td>
												))}
												<td className={`text-center cursor-pointer ${link.is_active ? 'bg-primary text-white' : 'bg-danger text-white'}`}
													title={t('brand_link_toggle_hint')}
													onClick={() => toggleActive(link)}>
													{link.is_active ? t('active') : t('inactive')}
												</td>
												<td>
													<div className="table-dropdown">
														<Dropdown>
															<Dropdown.Toggle variant="outline-primary">
																<i className="uil uil-ellipsis-v"></i>
															</Dropdown.Toggle>
															<Dropdown.Menu>
																<div className="table-dropdown-item" onClick={() => openEdit(link)}>
																	<i className="uil-edit-alt"></i>
																	{t('edit')}
																</div>
																<div className="table-dropdown-item text-danger"
																	onClick={() => {
																		setDeleteId(link.id)
																		setModalDelete(true)
																	}}>
																	<i className="uil-trash-alt"></i>
																	{t('delete')}
																</div>
															</Dropdown.Menu>
														</Dropdown>
													</div>
												</td>
											</SortableLinkRow>
										))}
									</SortableContext>
								</DndContext>
								{links.length === 0 &&
									<tr>
										<td colSpan={6 + CHANNELS.length} className="text-center text-muted">
											{t('brand_links_empty')}
										</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<Modal show={!!selectedItem} animation={false} centered scrollable
				onHide={() => setSelectedItem(null)}>
				<Modal.Header closeButton>
					<Modal.Title>
						<span className="d-inline-flex align-items-center gap-2">
							{selectedItem && <LinkIcon link={selectedItem} size={20} />}
							{selectedItem && typeName(selectedItem.type)}
						</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '500px' }}>
					{selectedItem &&
						<form autoComplete="off" onSubmit={(e) => { e.preventDefault(); save() }}>
							<div className="form-group">
								<label>{t('brand_link_url')}<span className="required-mark">*</span></label>
								<input type="text" className="form-control"
									placeholder={selectedMeta?.placeholder}
									value={selectedItem.url ?? ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'url': e.target.value })} />
								<small className="text-muted">{t('brand_link_url_hint')}</small>
							</div>

							<div className="form-group">
								<label>
									{t('brand_link_title')}
									{selectedItem.type === 'custom' && <span className="required-mark">*</span>}
								</label>
								<input type="text" className="form-control" maxLength={100}
									placeholder={selectedItem.type === 'custom' ? t('brand_link_title_placeholder') : typeName(selectedItem.type)}
									value={selectedItem.title ?? ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'title': e.target.value })} />
							</div>

							{selectedItem.type === 'custom' &&
								<div className="form-group">
									<label>{t('brand_link_icon')}</label>
									<div className="d-flex align-items-center gap-2">
										{selectedItem.icon_url &&
											<>
												<img src={selectedItem.icon_url} alt=""
													style={{ width: 32, height: 32, objectFit: 'contain' }} />
												<button type="button" className="btn btn-outline-danger btn-sm"
													onClick={() => setSelectedItem({ ...selectedItem, 'icon': null, 'icon_url': null })}>
													<i className="uil-times"></i>
												</button>
											</>
										}
										<input type="file" accept="image/*" className="form-control"
											onChange={(e) => uploadIcon(e)} />
									</div>
								</div>
							}

							<div className="form-group">
								<label>{t('brand_link_channels')}</label>
								{CHANNELS.map(channel => (
									<div key={channel} className="form-check">
										<input id={`brand-link-channel-${channel}`} type="checkbox" className="form-check-input"
											checked={selectedItem.channels.includes(channel)}
											onChange={() => toggleChannel(channel)} />
										<label className="form-check-label" htmlFor={`brand-link-channel-${channel}`}>
											{t(`brand_link_channel_${channel}`)}
										</label>
									</div>
								))}
							</div>

							<div className="form-check form-switch">
								<input id="brand-link-active" type="checkbox" className="form-check-input"
									checked={selectedItem.is_active}
									onChange={() => setSelectedItem({ ...selectedItem, 'is_active': !selectedItem.is_active })} />
								<label className="form-check-label" htmlFor="brand-link-active">
									{t('brand_link_show_to_guests')}
								</label>
							</div>
						</form>
					}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => setSelectedItem(null)}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary w-100"
							onClick={() => save()}>
							{t('save')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}
			/>
		</>
	)
}

export default BrandLinks
